import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOSchema from '../components/SEOSchema';
import MedicalReviewByline from '../components/MedicalReviewByline';
import {
  ClipboardList, Target, FileText, PlayCircle, Activity,
  SlidersHorizontal, Repeat, ArrowRight, Stethoscope, FlaskConical,
  UserCheck, Leaf, HeartPulse,
} from 'lucide-react';

const steps = [
  { icon: ClipboardList, title: 'Assessment', text: 'We review your medical history, current medications, lab reports, lifestyle and goals to understand your full health picture.' },
  { icon: Target, title: 'Goal Setting', text: 'Together we define realistic, measurable health goals, framed around your clinical context, not generic targets.' },
  { icon: FileText, title: 'Plan Development', text: 'Your medical lead designs a personalized nutrition and fitness plan that accounts for your conditions, preferences and routine.' },
  { icon: PlayCircle, title: 'Implementation', text: 'You begin the plan with clear guidance, structured delivery, and support to build consistent habits from day one.' },
  { icon: Activity, title: 'Monitoring', text: 'We track progress and relevant markers through regular check-ins and your reported data.' },
  { icon: SlidersHorizontal, title: 'Adjustments', text: 'Plans are reviewed and adapted based on your response, results and any changes in your health.' },
  { icon: Repeat, title: 'Long-Term Maintenance', text: 'We help you sustain results with maintenance strategies, periodic reviews and ongoing accountability.' },
];

const pillars = [
  { icon: Stethoscope, title: 'Doctor-Led Coaching', text: 'Every program is overseen by a qualified medical lead, not just a trainer or influencer.' },
  { icon: FlaskConical, title: 'Evidence-Based', text: 'Recommendations are grounded in clinical evidence and guidelines, adapted to real life.' },
  { icon: UserCheck, title: 'Individualized', text: 'Plans are built around your diagnosis, labs, medications and personal context.' },
  { icon: Leaf, title: 'Sustainable Change', text: 'We prioritize habits you can maintain over quick fixes that rebound.' },
  { icon: HeartPulse, title: 'Long-Term Optimization', text: 'The goal is durable health, not a temporary transformation.' },
];

const HowWeWorkPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>How We Work: Our Coaching Methodology | TCUBE FITNESS™</title>
      <meta name="description" content="The TCUBE FITNESS coaching methodology: assessment, goal setting, plan development, implementation, monitoring, adjustments and long-term maintenance, all doctor-led and evidence-based." />
      <link rel="canonical" href="https://tcubefitness.com/how-we-work" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="How We Work | TCUBE FITNESS™ Coaching Methodology"
      description="Our doctor-led, evidence-based coaching methodology from assessment to long-term maintenance."
      url="/how-we-work"
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'How We Work', path: '/how-we-work' }]}
    />

    <Header />

    <main id="main-content" className="pt-28 sm:pt-32 pb-20" data-testid="how-we-work-page">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Hero */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
            <Stethoscope className="w-4 h-4" aria-hidden="true" />
            Our Coaching Methodology
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            A Transparent, <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Doctor-Led Process</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mb-6">
            We don't believe in one-size-fits-all plans. Here is exactly how we work with you, from first assessment to long-term maintenance.
          </p>
          <MedicalReviewByline className="mx-auto" />
        </div>

        {/* UVP pillars */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {pillars.map((p, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5" data-testid={`uvp-pillar-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-3">
                  <p.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 7-step process */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">The 7-Step Journey</h2>
          <div className="relative">
            <div className="absolute left-6 top-2 bottom-2 w-px bg-gradient-to-b from-blue-600 via-cyan-600 to-emerald-600 hidden sm:block" aria-hidden="true"></div>
            <div className="space-y-5">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-5 items-start" data-testid={`methodology-step-${i}`}>
                  <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg">
                    <s.icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-blue-400 text-xs font-bold">STEP {i + 1}</span>
                    </div>
                    <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto text-center mt-16 bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-3xl p-8 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to start with a clear plan?</h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">Book a free consultation and we'll begin with a thorough assessment of your health and goals.</p>
          <Link
            to="/book-consultation?from=how-we-work"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-7 py-3.5 rounded-full font-bold transition-all shadow-lg shadow-blue-500/25"
            data-testid="how-we-work-cta"
          >
            Book a Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

export default HowWeWorkPage;
